import { useCallback, useEffect, useState } from "react";
import { Server, PlugZap, DownloadCloud, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";

const BLANK = { host: "", port: 22, username: "", password: "", folder: "/", source_name: "SFTP Partner", enabled: true };

export default function SftpImportCard() {
  const [form, setForm] = useState(BLANK);
  const [hasPassword, setHasPassword] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [testFiles, setTestFiles] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/feeds/sftp/config");
      if (r.data.configured) {
        setForm((f) => ({
          ...f,
          host: r.data.host || "",
          port: r.data.port || 22,
          username: r.data.username || "",
          folder: r.data.folder || "/",
          source_name: r.data.source_name || "SFTP Partner",
          enabled: r.data.enabled !== false,
        }));
        setHasPassword(r.data.has_password);
      }
      setLastImport(r.data.last_import);
    } catch (e) {
      console.warn("Failed to load SFTP config:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.host || !form.username) {
      toast.error("Host and username are required");
      return;
    }
    if (!form.password && !hasPassword) {
      toast.error("Enter the SFTP password");
      return;
    }
    setSaving(true);
    try {
      await api.put("/feeds/sftp/config", { ...form, port: parseInt(form.port, 10) || 22 });
      toast.success("SFTP settings saved");
      setForm((f) => ({ ...f, password: "" }));
      setHasPassword(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestFiles(null);
    try {
      const r = await api.post("/feeds/sftp/test");
      setTestFiles(r.data.files || []);
      toast.success(r.data.files?.length ? `Connected — found ${r.data.files.length} CSV file(s)` : "Connected, but no CSV files in that folder yet");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connection failed");
    } finally {
      setTesting(false);
    }
  };

  const importNow = async () => {
    setImporting(true);
    try {
      const r = await api.post("/feeds/sftp/import");
      const d = r.data;
      if (d.skipped && !d.parsed) {
        toast.info(typeof d.skipped === "string" ? d.skipped : "Nothing new to import");
      } else {
        toast.success(`Imported ${d.file}: ${d.inserted} new, ${d.updated} updated, ${d.marked_sold} marked sold${d.skipped ? `, ${d.skipped} skipped` : ""}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="border-2 border-soft bg-card p-6 md:p-8" data-testid="sftp-import-section">
      <div className="flex items-start gap-3">
        <Server className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-serif text-2xl text-primary">Pull Inventory From a Partner's SFTP</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Daily auto-import</span>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="sftp-enabled-switch" />
            </div>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the SFTP details the other website gives you. We check their folder once a day for a new
            inventory CSV and import it automatically — or press "Import Now" anytime.
          </p>

          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <Label htmlFor="sftp-host" className="text-xs">Host</Label>
              <Input id="sftp-host" placeholder="sftp.partner-site.com" value={form.host} onChange={set("host")} data-testid="sftp-host" />
            </div>
            <div>
              <Label htmlFor="sftp-port" className="text-xs">Port</Label>
              <Input id="sftp-port" type="number" value={form.port} onChange={set("port")} data-testid="sftp-port" />
            </div>
            <div>
              <Label htmlFor="sftp-user" className="text-xs">Username</Label>
              <Input id="sftp-user" value={form.username} onChange={set("username")} data-testid="sftp-username" />
            </div>
            <div className="lg:col-span-2">
              <Label htmlFor="sftp-pass" className="text-xs">Password {hasPassword && <span className="text-muted-foreground font-normal">(saved — leave blank to keep)</span>}</Label>
              <div className="relative">
                <Input id="sftp-pass" type={showPw ? "text" : "password"} placeholder={hasPassword ? "••••••••" : ""} value={form.password} onChange={set("password")} data-testid="sftp-password" />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="sftp-toggle-password">
                  {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="sftp-folder" className="text-xs">Folder</Label>
              <Input id="sftp-folder" placeholder="/outbound" value={form.folder} onChange={set("folder")} data-testid="sftp-folder" />
            </div>
            <div>
              <Label htmlFor="sftp-source" className="text-xs">Shows in imports as</Label>
              <Input id="sftp-source" value={form.source_name} onChange={set("source_name")} data-testid="sftp-source-name" />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={save} disabled={saving} className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/90" data-testid="sftp-save">
              {saving ? "Saving…" : "Save Settings"}
            </Button>
            <Button onClick={test} disabled={testing || !hasPassword} variant="outline" className="rounded-sm gap-1" data-testid="sftp-test">
              <PlugZap className="h-3.5 w-3.5" /> {testing ? "Connecting…" : "Test Connection"}
            </Button>
            <Button onClick={importNow} disabled={importing || !hasPassword} variant="outline" className="rounded-sm gap-1" data-testid="sftp-import-now">
              <DownloadCloud className="h-3.5 w-3.5" /> {importing ? "Importing…" : "Import Now"}
            </Button>
          </div>

          {testFiles && (
            <div className="mt-3 text-xs bg-secondary/50 border border-soft p-3" data-testid="sftp-test-results">
              {testFiles.length === 0 ? (
                <span className="text-muted-foreground">Connected — no CSV files in that folder yet.</span>
              ) : (
                <>
                  <span className="font-medium text-primary">Files found (newest first):</span>
                  <ul className="mt-1 space-y-0.5 text-muted-foreground">
                    {testFiles.map((f) => (
                      <li key={f.name}>{f.name} — {(f.size / 1024).toFixed(1)} KB, {new Date(f.mtime * 1000).toLocaleString()}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {lastImport && (
            <div className="mt-3 text-xs text-muted-foreground bg-secondary/50 border border-soft p-3" data-testid="sftp-last-import">
              <span className="font-medium text-primary">Last SFTP import ({lastImport.file}):</span>{" "}
              {new Date(lastImport.created_at).toLocaleString()} — {lastImport.inserted} new, {lastImport.updated} updated,{" "}
              {lastImport.marked_sold} marked sold{lastImport.skipped ? `, ${lastImport.skipped} skipped` : ""}.
            </div>
          )}

          <p className="mt-3 text-xs text-muted-foreground">
            The partner's file needs the same columns as the DMS feed above (VIN, Make, Model, Year, Price + optional extras).
            If the partner instead needs a folder to drop files <em>into</em>, give them your Couchdrop SFTP folder —
            anything dropped there already forwards to your secure drop URL above.
          </p>
        </div>
      </div>
    </section>
  );
}
